import type { Route } from '@angular/router';

import { FuneralWish } from './funeral-wish';
import { Guardianship } from './guardianship';
import { JourneyDashboard } from './journey-dashboard';
import { JourneyDocuments } from './journey-documents';
import {
  draftGuard,
  frenchCompleteGuard,
  privateScreenGuard,
  unsavedChangesGuard,
} from './journey.guards';
import { JourneyNavigation } from './journey-navigation';
import { JourneyOnline } from './journey-online';
import { JOURNEY_PATHS } from './journey.paths';
import { JourneyStart } from './journey-start';
import { JourneyStore } from './journey.store';
import type { JourneyView } from './journey.types';
import { PrivateCollections } from './private-collections';
import { PublicJourneyComponent } from './public-journey.component';
import { HomeScreenComponent } from './screens/home-screen.component';
import { SpiritualBalance } from './spiritual-balance';

/** What a screen's address says about it: which screen it is, read back by the store. */
const shows = (view: JourneyView) => ({ path: JOURNEY_PATHS[view], data: { view } });

/**
 * The journey: one frame, and one address per screen inside it. The first arrival is drawn at
 * once; every other screen is fetched the first time it is needed, or earlier, while the phone is
 * idle. What the screens share is provided here, with the journey's route.
 */
export const JOURNEY_ROUTE: Route = {
  path: '',
  component: PublicJourneyComponent,
  canActivateChild: [frenchCompleteGuard],
  providers: [
    JourneyStore,
    JourneyStart,
    JourneyDocuments,
    PrivateCollections,
    SpiritualBalance,
    Guardianship,
    FuneralWish,
    JourneyDashboard,
    JourneyNavigation,
    JourneyOnline,
  ],
  children: [
    { ...shows('home'), pathMatch: 'full', component: HomeScreenComponent },
    {
      ...shows('profile'),
      loadComponent: () =>
        import('./screens/profile-screen.component').then((m) => m.ProfileScreenComponent),
    },
    {
      ...shows('dashboard'),
      canMatch: [draftGuard],
      loadComponent: () =>
        import('./screens/dashboard-screen.component').then((m) => m.DashboardScreenComponent),
    },
    {
      ...shows('dashboard-part'),
      canMatch: [draftGuard],
      loadComponent: () =>
        import('./screens/dashboard-part-screen.component').then(
          (m) => m.DashboardPartScreenComponent,
        ),
    },
    {
      ...shows('funeral'),
      canMatch: [draftGuard],
      canDeactivate: [unsavedChangesGuard],
      loadComponent: () =>
        import('./screens/funeral-screen.component').then((m) => m.FuneralScreenComponent),
    },
    // The wasi's screen was a level of the wishes before it became a tab: old links still arrive.
    { path: 'volontes/wasi', redirectTo: JOURNEY_PATHS.executor, pathMatch: 'full' },
    {
      ...shows('executor'),
      canMatch: [draftGuard],
      canDeactivate: [unsavedChangesGuard],
      loadComponent: () =>
        import('./screens/executor-screen.component').then((m) => m.ExecutorScreenComponent),
    },
    {
      ...shows('executor-notice'),
      canMatch: [draftGuard],
      loadComponent: () =>
        import('./screens/executor-notice-screen.component').then(
          (m) => m.ExecutorNoticeScreenComponent,
        ),
    },
    {
      ...shows('executor-access'),
      canMatch: [draftGuard],
      loadComponent: () =>
        import('./screens/executor-access-screen.component').then(
          (m) => m.ExecutorAccessScreenComponent,
        ),
    },
    {
      ...shows('guardianship'),
      canMatch: [privateScreenGuard],
      canDeactivate: [unsavedChangesGuard],
      loadComponent: () =>
        import('./screens/guardianship-screen.component').then(
          (m) => m.GuardianshipScreenComponent,
        ),
    },
    {
      ...shows('estate'),
      canMatch: [privateScreenGuard],
      canDeactivate: [unsavedChangesGuard],
      loadComponent: () =>
        import('./screens/estate-screen.component').then((m) => m.EstateScreenComponent),
    },
    {
      ...shows('spiritual-balance'),
      canMatch: [privateScreenGuard],
      loadComponent: () =>
        import('./screens/spiritual-screen.component').then((m) => m.SpiritualScreenComponent),
    },
    {
      ...shows('private-item'),
      canMatch: [privateScreenGuard],
      canDeactivate: [unsavedChangesGuard],
      loadComponent: () =>
        import('./screens/private-item-screen.component').then((m) => m.PrivateItemScreenComponent),
    },
    {
      ...shows('private-collection'),
      canMatch: [privateScreenGuard],
      loadComponent: () =>
        import('./screens/private-collection-screen.component').then(
          (m) => m.PrivateCollectionScreenComponent,
        ),
    },
    {
      ...shows('unlock'),
      canMatch: [draftGuard],
      loadComponent: () =>
        import('./screens/unlock-screen.component').then((m) => m.UnlockScreenComponent),
    },
    // Creating the phrase draws its own screen, frame and all: the journey shows it itself.
    { ...shows('passphrase-creation'), canMatch: [draftGuard], children: [] },
    {
      ...shows('documents'),
      canMatch: [draftGuard],
      loadComponent: () =>
        import('./screens/documents-screen.component').then((m) => m.DocumentsScreenComponent),
    },
    {
      ...shows('testament-preview'),
      canMatch: [draftGuard],
      loadComponent: () =>
        import('./screens/testament-preview-screen.component').then(
          (m) => m.TestamentPreviewScreenComponent,
        ),
    },
    {
      ...shows('emergency'),
      canMatch: [draftGuard],
      loadComponent: () =>
        import('./screens/emergency-screen.component').then((m) => m.EmergencyScreenComponent),
    },
    {
      ...shows('in-your-country'),
      canMatch: [draftGuard],
      loadComponent: () =>
        import('./screens/in-your-country-screen.component').then(
          (m) => m.InYourCountryScreenComponent,
        ),
    },
    {
      ...shows('settings'),
      canMatch: [draftGuard],
      loadComponent: () =>
        import('./screens/settings-screen.component').then((m) => m.SettingsScreenComponent),
    },
    {
      ...shows('settings-language'),
      canMatch: [draftGuard],
      loadComponent: () =>
        import('./screens/language-screen.component').then((m) => m.LanguageScreenComponent),
    },
    {
      ...shows('security'),
      canMatch: [draftGuard],
      loadComponent: () =>
        import('./screens/security-screen.component').then((m) => m.SecurityScreenComponent),
    },
    {
      ...shows('security-change'),
      canMatch: [draftGuard],
      loadComponent: () =>
        import('./screens/security-change-screen.component').then(
          (m) => m.SecurityChangeScreenComponent,
        ),
    },
    {
      ...shows('security-forgot'),
      canMatch: [draftGuard],
      loadComponent: () =>
        import('./screens/security-forgot-screen.component').then(
          (m) => m.SecurityForgotScreenComponent,
        ),
    },
    {
      ...shows('backup'),
      canMatch: [draftGuard],
      loadComponent: () =>
        import('./screens/backup-screen.component').then((m) => m.BackupScreenComponent),
    },
    {
      ...shows('backup-ready'),
      canMatch: [draftGuard],
      loadComponent: () =>
        import('./screens/backup-ready-screen.component').then((m) => m.BackupReadyScreenComponent),
    },
    {
      ...shows('backup-online'),
      canMatch: [draftGuard],
      loadComponent: () =>
        import('./screens/backup-online-screen.component').then(
          (m) => m.BackupOnlineScreenComponent,
        ),
    },
    {
      ...shows('backup-conflict'),
      canMatch: [draftGuard],
      loadComponent: () =>
        import('./screens/backup-conflict-screen.component').then(
          (m) => m.BackupConflictScreenComponent,
        ),
    },
    // The terms and the policy: readable by anyone, before any will exists, with no phrase.
    {
      ...shows('legal-terms'),
      loadComponent: () =>
        import('./screens/legal-screen.component').then((m) => m.LegalScreenComponent),
    },
    {
      ...shows('legal-privacy'),
      loadComponent: () =>
        import('./screens/legal-screen.component').then((m) => m.LegalScreenComponent),
    },
    {
      ...shows('restore'),
      loadComponent: () =>
        import('./screens/restore-screen.component').then((m) => m.RestoreScreenComponent),
    },
    {
      ...shows('more'),
      canMatch: [draftGuard],
      loadComponent: () =>
        import('./screens/more-screen.component').then((m) => m.MoreScreenComponent),
    },
    {
      ...shows('support'),
      loadComponent: () =>
        import('./screens/support-screen.component').then((m) => m.SupportScreenComponent),
    },
    {
      ...shows('about'),
      canMatch: [draftGuard],
      loadComponent: () =>
        import('./screens/about-screen.component').then((m) => m.AboutScreenComponent),
    },
    {
      ...shows('contact'),
      canMatch: [draftGuard],
      loadComponent: () =>
        import('./screens/contact-screen.component').then((m) => m.ContactScreenComponent),
    },
    {
      ...shows('wasi'),
      loadComponent: () =>
        import('./screens/wasi-screen.component').then((m) => m.WasiScreenComponent),
    },
    // An address that names no screen leads to the first one.
    { path: '**', redirectTo: '' },
  ],
};
